import { gql } from '@apollo/client';

const queryText = `
query GetMapListings(
  $listingOffset: Int!
  $storefrontOffset: Int!
  $limit: Int!
  $userId: ID
  $order: [String!]
  $filters: ListingFiltersInput
  $personalCountFilters: ListingFiltersInput
  $wantedCountFilters: ListingFiltersInput
  $storefrontItemsCountFilters: ListingFiltersInput
  $eventsCountFilters: ListingFiltersInput
  $storefrontFilters: StoreFrontFiltersInput
  $skip: Boolean!
) {
  personalListingsCount: listings(filters: $personalCountFilters) {
    meta {
      pagination {
        total
      }
    }
  }
  wantedListingsCount: listings(filters: $wantedCountFilters) {
    meta {
      pagination {
        total
      }
    }
  }
  storefrontItemsCount: listings(filters: $storefrontItemsCountFilters) {
    meta {
      pagination {
        total
      }
    }
  }
  eventsCount: listings(filters: $eventsCountFilters) {
    meta {
      pagination {
        total
      }
    }
  }
  listings(
    pagination: { start: $listingOffset, limit: $limit }
    sort: $order
    filters: $filters
  ) {
    data {
      id
      attributes {
        image(pagination: { limit: 1 }, sort: ["createdAt:DESC"]) {
          data {
            attributes {
              url
              formats
            }
          }
        }
        type
        name
        description
        eventStartDate
        eventEndDate
        eventInfoUrl
        isFreeEvent
        ticketTiers {
          name
          priceInUSD
          priceInDoge
        }
        location {
          address
          latitude
          longitude
        }
        price {
          alternatePriceOptions
          priceType
          priceInUSD
          priceInDoge
        }
        store_front {
          data {
            id
            attributes {
              name
              location {
                address
                latitude
                longitude
              }
            }
          }
        }
        saved_listings(filters: { user: { id: { eq: $userId } } }) @skip(if: $skip) {
          data {
            id
            attributes {
              user {
                data {
                  id
                }
              }
            }
          }
        }
        user {
          data {
            id
            attributes {
              name
              username
              type
            }
          }
        }
        publishedAt
      }
    }
  }
  storeFrontsCount: storeFronts(filters: $storefrontFilters) {
    meta {
      pagination {
        total
      }
    }
  }
  storeFronts(
    pagination: { start: $storefrontOffset, limit: $limit }
    sort: $order
    filters: $storefrontFilters
  ) {
    data {
      id
      attributes {
        name
        description
        reviewAverage
        reviewCount
        logo {
          data {
            attributes {
              url
            }
          }
        }
        image(pagination: { limit: 1 }, sort: ["createdAt:DESC"]) {
          data {
            attributes {
              url
              formats
            }
          }
        }
        user {
          data {
            id
            attributes {
              name
              username
              type
            }
          }
        }
        saved_storefronts(filters: { user: { id: { eq: $userId } } }) @skip(if: $skip) {
          data {
            id
            attributes {
              user {
                data {
                  id
                }
              }
            }
          }
        }
        location {
          address
          latitude
          longitude
        }
        publishedAt
        claimStatus
      }
    }
  }
}
`;

const mapQueryText = `
query GetMapListings(
  $listingOffset: Int!
  $storefrontOffset: Int!
  $limit: Int!
  $userId: ID
  $order: [String!]
  $filters: ListingFiltersInput
  $storefrontFilters: StoreFrontFiltersInput
  $skip: Boolean!
) {

  listings(
    pagination: { start: $listingOffset, limit: $limit }
    sort: $order
    filters: $filters
  ) {
    data {
      id
      attributes {
        image(pagination: { limit: 1 }, sort: ["createdAt:DESC"]) {
          data {
            attributes {
              url
              formats
            }
          }
        }
        type
        name
        eventStartDate
        eventEndDate
        eventInfoUrl
        isFreeEvent
        ticketTiers {
          name
          priceInUSD
          priceInDoge
        }
        location {
          address
          latitude
          longitude
        }
        price {
          alternatePriceOptions
          priceType
          priceInUSD
          priceInDoge
        }
        store_front {
          data {
            id
            attributes {
              name
              location {
                address
                latitude
                longitude
              }
            }
          }
        }
        saved_listings(filters: { user: { id: { eq: $userId } } }) @skip(if: $skip) {
          data {
            id
            attributes {
              user {
                data {
                  id
                }
              }
            }
          }
        }
        user {
          data {
            id
            attributes {
              name
              username
            }
          }
        }
        publishedAt
      }
    }
  }

  storeFronts(
    pagination: { start: $storefrontOffset, limit: $limit }
    sort: $order
    filters: $storefrontFilters
  ) {
    data {
      id
      attributes {
        name
        reviewAverage
        reviewCount
        logo {
          data {
            attributes {
              url
            }
          }
        }
        image(pagination: { limit: 1 }, sort: ["createdAt:DESC"]) {
          data {
            attributes {
              url
              formats
            }
          }
        }
        user {
          data {
            id
            attributes {
              name
              username
            }
          }
        }
        claimStatus
        verifiedAt
        communityReporter {
          data {
            id
            attributes {
              username
              name
            }
          }
        }
        saved_storefronts(filters: { user: { id: { eq: $userId } } }) @skip(if: $skip) {
          data {
            id
            attributes {
              user {
                data {
                  id
                }
              }
            }
          }
        }
        location {
          address
          latitude
          longitude
        }
        publishedAt
      }
    }
  }
}
`;

// Page-only variant for Load more: the SAME paged listings/storeFronts fields
// (identical args -> identical cache keys, so pages merge into the same lists)
// but WITHOUT the five count aggregates - those don't change between pages,
// and re-running them on every click was pure backend load.
const pageQueryText = `
query GetMapListingsPage(
  $listingOffset: Int!
  $storefrontOffset: Int!
  $limit: Int!
  $userId: ID
  $order: [String!]
  $filters: ListingFiltersInput
  $storefrontFilters: StoreFrontFiltersInput
  $skip: Boolean!
) {
  listings(
    pagination: { start: $listingOffset, limit: $limit }
    sort: $order
    filters: $filters
  ) {
    data {
      id
      attributes {
        image(pagination: { limit: 1 }, sort: ["createdAt:DESC"]) {
          data {
            attributes {
              url
              formats
            }
          }
        }
        type
        name
        description
        eventStartDate
        eventEndDate
        eventInfoUrl
        isFreeEvent
        ticketTiers {
          name
          priceInUSD
          priceInDoge
        }
        location {
          address
          latitude
          longitude
        }
        price {
          alternatePriceOptions
          priceType
          priceInUSD
          priceInDoge
        }
        store_front {
          data {
            id
            attributes {
              name
              location {
                address
                latitude
                longitude
              }
            }
          }
        }
        saved_listings(filters: { user: { id: { eq: $userId } } }) @skip(if: $skip) {
          data {
            id
            attributes {
              user {
                data {
                  id
                }
              }
            }
          }
        }
        user {
          data {
            id
            attributes {
              name
              username
              type
            }
          }
        }
        publishedAt
      }
    }
  }
  storeFronts(
    pagination: { start: $storefrontOffset, limit: $limit }
    sort: $order
    filters: $storefrontFilters
  ) {
    data {
      id
      attributes {
        name
        description
        reviewAverage
        reviewCount
        logo {
          data {
            attributes {
              url
            }
          }
        }
        image(pagination: { limit: 1 }, sort: ["createdAt:DESC"]) {
          data {
            attributes {
              url
              formats
            }
          }
        }
        user {
          data {
            id
            attributes {
              name
              username
              type
            }
          }
        }
        saved_storefronts(filters: { user: { id: { eq: $userId } } }) @skip(if: $skip) {
          data {
            id
            attributes {
              user {
                data {
                  id
                }
              }
            }
          }
        }
        location {
          address
          latitude
          longitude
        }
        publishedAt
        claimStatus
      }
    }
  }
}
`;

// Slim pin payload for WIDE zooms: dots and clusters need coordinates and a
// type, not images/descriptions/saved-state. Price + store_front location stay
// so storefront-item pins place correctly and popups stay coherent.
const mapSlimQueryText = `
query GetMapPinsSlim(
  $listingOffset: Int!
  $storefrontOffset: Int!
  $limit: Int!
  $order: [String!]
  $filters: ListingFiltersInput
  $storefrontFilters: StoreFrontFiltersInput
) {
  listings(
    pagination: { start: $listingOffset, limit: $limit }
    sort: $order
    filters: $filters
  ) {
    data {
      id
      attributes {
        type
        name
        eventStartDate
        eventEndDate
        isFreeEvent
        location {
          latitude
          longitude
        }
        price {
          alternatePriceOptions
          priceType
          priceInUSD
          priceInDoge
        }
        store_front {
          data {
            id
            attributes {
              name
              location {
                latitude
                longitude
              }
            }
          }
        }
      }
    }
  }
  storeFronts(
    pagination: { start: $storefrontOffset, limit: $limit }
    sort: $order
    filters: $storefrontFilters
  ) {
    data {
      id
      attributes {
        name
        location {
          latitude
          longitude
        }
      }
    }
  }
}
`;

export const GET_LISTINGS_AND_STOREFRONTS = gql`
  ${queryText}
`;

export const GET_MAP_LISTINGS_PAGE = gql`
  ${pageQueryText}
`;

export const GET_LISTINGS_AND_STOREFRONTS_STR = mapQueryText;
export const GET_MAP_PINS_SLIM_STR = mapSlimQueryText;
